"use client"

import { useState, useEffect } from 'react'
import Image from 'next/image'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { blurDataURLs, imageSizes } from '@/lib/image-utils'

interface GalleryImage {
  src: string
  alt: string
}

interface PaginatedGalleryProps {
  images: GalleryImage[]
  itemsPerPageDesktop?: number
  itemsPerPageMobile?: number
}

export function PaginatedGallery({ 
  images, 
  itemsPerPageDesktop = 6,
  itemsPerPageMobile = 3 
}: PaginatedGalleryProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageDesktop)
  const [isMobile, setIsMobile] = useState(false)

  // Check if mobile and adjust items per page
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      setItemsPerPage(mobile ? itemsPerPageMobile : itemsPerPageDesktop)
      
      // Reset to page 1 if current page would be out of bounds
      const newTotalPages = Math.ceil(images.length / (mobile ? itemsPerPageMobile : itemsPerPageDesktop))
      if (currentPage > newTotalPages) {
        setCurrentPage(1)
      }
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [currentPage, images.length, itemsPerPageDesktop, itemsPerPageMobile])

  // Calculate pagination
  const totalPages = Math.ceil(images.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentImages = images.slice(startIndex, endIndex)

  // Handle page changes
  const goToPage = (page: number) => {
    setCurrentPage(page)
    // Scroll to top of gallery
    const gallerySection = document.getElementById('gallery-section')
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  const goToPrevious = () => {
    if (currentPage > 1) goToPage(currentPage - 1)
  }

  const goToNext = () => {
    if (currentPage < totalPages) goToPage(currentPage + 1)
  }

  if (images.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        No images available in the gallery
      </div>
    )
  }

  return (
    <div id="gallery-section" className="space-y-8">
      {/* Gallery Grid */}
      <div className={`grid gap-4 ${
        isMobile 
          ? 'grid-cols-1' 
          : 'md:grid-cols-2 lg:grid-cols-3'
      }`}>
        {currentImages.map((image, index) => (
          <div 
            key={`${image.src}-${startIndex + index}`}
            className="relative h-64 md:h-80 rounded-lg overflow-hidden shadow-lg group cursor-pointer animate-fade-in bg-gray-100"
            style={{
              animationDelay: `${index * 100}ms`
            }}
          >
            <Image
              src={image.src}
              alt={image.alt}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-110"
              sizes={imageSizes.gallery.sizes}
              quality={80}
              priority={currentPage === 1 && index < 3} // Priority load first 3 images on first page only
              loading={currentPage === 1 && index < 3 ? 'eager' : 'lazy'}
            />
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          {/* Previous Button */}
          <Button
            onClick={goToPrevious}
            disabled={currentPage === 1}
            variant="outline"
            size="icon"
            className="h-10 w-10"
            aria-label="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1">
            {/* Always show first page */}
            <Button
              onClick={() => goToPage(1)}
              variant={currentPage === 1 ? "default" : "outline"}
              className={`h-10 w-10 ${currentPage === 1 ? 'bg-[#c88546] hover:bg-[#b87439]' : ''}`}
            >
              1
            </Button>

            {/* Show dots if there are pages between first and current */}
            {currentPage > 3 && (
              <span className="px-2 text-gray-400">...</span>
            )}

            {/* Show current page and neighbors (if not first or last) */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(page => {
                if (page === 1 || page === totalPages) return false
                return Math.abs(page - currentPage) <= 1
              })
              .map(page => (
                <Button
                  key={page}
                  onClick={() => goToPage(page)}
                  variant={currentPage === page ? "default" : "outline"}
                  className={`h-10 w-10 ${currentPage === page ? 'bg-[#c88546] hover:bg-[#b87439]' : ''}`}
                >
                  {page}
                </Button>
              ))}

            {/* Show dots if there are pages between current and last */}
            {currentPage < totalPages - 2 && (
              <span className="px-2 text-gray-400">...</span>
            )}

            {/* Always show last page (if more than 1 page) */}
            {totalPages > 1 && (
              <Button
                onClick={() => goToPage(totalPages)}
                variant={currentPage === totalPages ? "default" : "outline"}
                className={`h-10 w-10 ${currentPage === totalPages ? 'bg-[#c88546] hover:bg-[#b87439]' : ''}`}
              >
                {totalPages}
              </Button>
            )}
          </div>

          {/* Next Button */}
          <Button
            onClick={goToNext}
            disabled={currentPage === totalPages}
            variant="outline"
            size="icon"
            className="h-10 w-10"
            aria-label="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Page Info */}
      {totalPages > 1 && (
        <div className="text-center text-sm text-gray-600">
          Showing {startIndex + 1}-{Math.min(endIndex, images.length)} of {images.length} images
        </div>
      )}
    </div>
  )
}